/**
 * Information Requests (IRL) overview card — view-model resolver.
 * Backend contract (planned): project.irl_overview or GET …/overview/irl/
 *
 * Expected API shape (draft):
 * {
 *   state: 'draft_suggested' | 'unassigned' | 'awaiting_analysis' | 'findings_ready' | 'in_progress' | 'not_created',
 *   request_count?: number,
 *   suggested_count?: number,
 *   eligible_document_count?: number,
 *   potential_answers_count?: number,
 *   follow_up_questions_count?: number,
 *   completed_count?: number,
 *   outstanding_count?: number,
 * }
 */

export const IRL_OVERVIEW_STATES = {
    REQUIRES_WORKSTREAMS: 'requires_workstreams',
    NOT_CREATED: 'not_created',
    DRAFT_SUGGESTED: 'draft_suggested',
    UNASSIGNED: 'unassigned',
    AWAITING_ANALYSIS: 'awaiting_analysis',
    FINDINGS_READY: 'findings_ready',
    IN_PROGRESS: 'in_progress',
    ACTIVE_PROGRESS: 'active_progress',
};

export const IRL_OVERVIEW_PREVIEW_QUERY = 'overviewIrlPreview';

const PREVIEW_STATES = new Set([
    IRL_OVERVIEW_STATES.DRAFT_SUGGESTED,
    IRL_OVERVIEW_STATES.UNASSIGNED,
    IRL_OVERVIEW_STATES.AWAITING_ANALYSIS,
    IRL_OVERVIEW_STATES.FINDINGS_READY,
]);

/** Mock figures until overview IRL API ships — used only with ?overviewIrlPreview= */
export const IRL_OVERVIEW_PREVIEW_FIXTURES = {
    [IRL_OVERVIEW_STATES.DRAFT_SUGGESTED]: {
        suggestedCount: 34,
    },
    [IRL_OVERVIEW_STATES.UNASSIGNED]: {
        requestCount: 34,
    },
    [IRL_OVERVIEW_STATES.AWAITING_ANALYSIS]: {
        requestCount: 34,
        eligibleDocumentCount: 8,
    },
    [IRL_OVERVIEW_STATES.FINDINGS_READY]: {
        potentialAnswersCount: 18,
        followUpQuestionsCount: 6,
    },
};

export function readIrlOverviewPreviewOverride() {
    if (typeof window === 'undefined') return null;
    const value = new URLSearchParams(window.location.search).get(IRL_OVERVIEW_PREVIEW_QUERY);
    if (!value || !PREVIEW_STATES.has(value)) return null;
    return value;
}

function pickApiIrlOverview(project) {
    return project?.irl_overview || project?.overview?.information_requests || project?.information_requests_overview || null;
}

function mapApiState(api) {
    const raw = api?.state || api?.status;
    if (!raw) return null;
    const normalized = String(raw).toLowerCase().replace(/-/g, '_');
    if (Object.values(IRL_OVERVIEW_STATES).includes(normalized)) return normalized;
    return null;
}

function num(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

export function resolveIrlOverviewCard({
    project,
    hasWorkstreams,
    vdrConfigured,
    vdrPopulated,
    documentCount,
    irlCompleted,
    irlOutstanding,
    previewOverride = readIrlOverviewPreviewOverride(),
}) {
    const api = pickApiIrlOverview(project);
    const irlTotal = irlCompleted + irlOutstanding;
    const hasIrl = irlTotal > 0;
    const fixture = previewOverride ? IRL_OVERVIEW_PREVIEW_FIXTURES[previewOverride] : null;
    const usesDummyData = Boolean(previewOverride && fixture);

    let state = previewOverride || mapApiState(api);

    if (!state) {
        if (!hasWorkstreams) state = IRL_OVERVIEW_STATES.REQUIRES_WORKSTREAMS;
        else if (!hasIrl) state = IRL_OVERVIEW_STATES.NOT_CREATED;
        else state = IRL_OVERVIEW_STATES.IN_PROGRESS;
    }

    const requestCount = num(api?.request_count, fixture?.requestCount ?? (hasIrl ? irlTotal : 0));
    const suggestedCount = num(api?.suggested_count, fixture?.suggestedCount ?? 0);
    const eligibleDocs = num(
        api?.eligible_document_count,
        fixture?.eligibleDocumentCount ?? (vdrPopulated ? Math.min(documentCount, 8) : 0),
    );
    const potentialAnswers = num(api?.potential_answers_count, fixture?.potentialAnswersCount ?? 0);
    const followUps = num(api?.follow_up_questions_count, fixture?.followUpQuestionsCount ?? 0);

    const base = {
        state,
        usesDummyData,
        navigateTo: '/irl',
        highlight: false,
        badge: null,
        metric: null,
        lines: [],
        actionLabel: 'Create initial IRL →',
        tall: false,
        showRecentActivity: false,
    };

    switch (state) {
        case IRL_OVERVIEW_STATES.REQUIRES_WORKSTREAMS:
            return {
                ...base,
                navigateTo: '/dataroom',
                metric: { text: 'Requires workstreams', size: 'sm' },
                lines: [{ text: 'Set up project workstreams before creating the initial Information Request List.', variant: 'body' }],
                actionLabel: 'Set up workstreams →',
            };

        case IRL_OVERVIEW_STATES.NOT_CREATED:
            return {
                ...base,
                metric: { text: 'Not created', size: 'sm' },
                lines: [
                    {
                        text: vdrConfigured
                            ? 'Create initial IRL. VDR is ready for upload.'
                            : vdrPopulated
                              ? `${documentCount} project documents are available to inform the request list.`
                              : 'Create the initial IRL using confirmed project context.',
                        variant: 'body',
                    },
                ],
                actionLabel: 'Create initial IRL →',
            };

        case IRL_OVERVIEW_STATES.DRAFT_SUGGESTED:
            return {
                ...base,
                highlight: 'accent',
                badge: { label: 'Needs review', tone: 'review' },
                metric: { text: `${suggestedCount || requestCount || 34} suggested`, size: 'lg' },
                lines: [{ text: 'Review Lens suggestions before publishing the request list.', variant: 'body' }],
                actionLabel: 'Review requests →',
                tall: true,
            };

        case IRL_OVERVIEW_STATES.UNASSIGNED:
            return {
                ...base,
                badge: { label: 'Needs action', tone: 'action' },
                metric: { text: `${requestCount || 34} requests`, size: 'lg' },
                lines: [{ text: 'Not assigned', variant: 'body' }],
                actionLabel: 'Assign requests →',
                tall: true,
            };

        case IRL_OVERVIEW_STATES.AWAITING_ANALYSIS:
            return {
                ...base,
                metric: { text: `${requestCount || 34} requests`, size: 'lg' },
                lines: [
                    { text: `${eligibleDocs || 8} eligible documents available`, variant: 'body' },
                    {
                        text: 'Existing evidence has not yet been checked against these requests.',
                        variant: 'small',
                    },
                ],
                actionLabel: 'Analyse documents →',
                tall: true,
            };

        case IRL_OVERVIEW_STATES.FINDINGS_READY:
            return {
                ...base,
                highlight: 'accent',
                badge: { label: 'Needs review', tone: 'review' },
                metric: { text: `${potentialAnswers || 18} potential answers found`, size: 'md' },
                lines: [{ text: `${followUps || 6} follow-up questions suggested`, variant: 'body' }],
                actionLabel: 'Review findings →',
                tall: true,
                showRecentActivity: true,
            };

        case IRL_OVERVIEW_STATES.IN_PROGRESS:
        default:
            return {
                ...base,
                metric: { text: 'In progress', size: 'sm' },
                lines: [{ text: `${irlCompleted} complete · ${irlOutstanding} outstanding`, variant: 'body' }],
                actionLabel: 'Open Information Requests →',
            };
    }
}

/** Mature overview — segmented / summary progress (Figma 6849:6400 · 6849:7411) */
export function buildActiveProgressIrlCard(config = {}) {
    const answered = num(config.answered, 0);
    const inProgress = num(config.inProgress, 0);
    const outstanding = num(config.outstanding, 0);
    const requestCount = num(config.requestCount, answered + inProgress + outstanding);
    const overdueCount = num(config.overdueCount, 0);
    const progressDisplay = config.progressDisplay === 'summary' ? 'summary' : 'legend';

    const summaryText = `${answered} answered · ${outstanding} outstanding · ${overdueCount} overdue`;

    return {
        state: IRL_OVERVIEW_STATES.ACTIVE_PROGRESS,
        usesDummyData: Boolean(config.usesDummyData),
        navigateTo: '/irl',
        highlight: false,
        badge: null,
        headerMeta: config.lastResponseLabel || null,
        overdueCount: overdueCount > 0 ? overdueCount : null,
        metric: { text: `${requestCount} requests`, size: 'lg' },
        progress: {
            answered,
            inProgress,
            outstanding,
            display: progressDisplay,
            summaryText,
        },
        lines: [],
        actionLabel: 'Open requests →',
        tall: true,
        showRecentActivity: false,
    };
}
